'use client';

/**
 * ObeyaCostPlanRequirementsCard — read-only requirement glance list for
 * the Cost Plan rail surface: label, candidate count, qty/value
 * summary, click-to-focus. Shares the `costplan_pivot` data source
 * (`group_by="line"`) with `ObeyaCostPlanLinesCard`, mode1 canvas's full
 * plan editor, but is a structurally separate component — not that same
 * card with props toggled off.
 *
 * Why separate rather than a flag: an earlier version of the rail mount
 * reused `ObeyaCostPlanLinesCard` behind two opt-in props (`compact`,
 * `interactive`) that only trimmed chrome and added the click-to-focus
 * affordance built here — neither prop suppressed that card's qty-edit,
 * expand-to-leaf, or room-assignment code, so the rail accidentally
 * became a full plan editor rather than the glance-and-pick summary it
 * was meant to be. This component contains none of that code at all —
 * not hidden behind a flag, structurally absent — so no prop or data
 * shape fed to it can ever make it start editing.
 *
 * Click-to-focus — the one interaction this card owns — sets
 * `useFocusedRequirementStore`'s `focusedSourceLineSid` to the clicked
 * group's requirement (its pivot key IS the source_line_sid in Line
 * mode — see `_pivot_key` in costplan_pivot.py), so a product tile's
 * "Add to Cost Plan" affordance elsewhere on the rail knows which
 * requirement to append to. A group keyed by the `no_source_line:`
 * fallback (a line with no traceable schedule requirement) has nothing
 * real to focus, so it's excluded.
 *
 * Always Line mode — the rail's only use case is "which requirement am
 * I filling," not Slot/Vendor rollups, so there's no group-by toggle.
 *
 * @see klyde_edge/klyde_intelligence/projects/handler.py — costplan_pivot
 * @see klyde_ipe/components/copilot/rail-insights/cost-plan-surface.tsx
 * @see klyde_ipe/components/canvas/cards/obeya/costplan-lines-card.tsx
 *   — the full editor this deliberately does not reuse
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useEffect, useMemo, useState } from 'react';
import { Search, Crosshair } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { usePinnedCostPlanStore } from '@/lib/stores/pinned-costplan-store';
import { useFocusedRequirementStore } from '@/lib/stores/focused-requirement-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import {
  UNASSIGNED_KEY, NO_SOURCE_LINE_PREFIX,
} from './costplan-pivot-shared';
import type { PivotGroup, CostPlanPivotData } from './costplan-pivot-shared';

const ROW = 'px-3 py-2';

export interface ObeyaCostPlanRequirementsCardProps {
  /**
   * Explicit fetch-input override — same card, parameterized for a
   * second surface (mental move 6: a self-fetching card takes its
   * fetch inputs as live props, never frozen internal state). When
   * set, this wins over `useViewportSelection()`'s page selection —
   * mirrors `ObeyaCostPlanLinesCard`/`ObeyaCostPlanComparisonCard`'s
   * `mcpIdOverride` for consistency. Omit to keep the rail's original
   * behaviour (reads page selection via useViewportSelection, falls
   * back to the pinned-costplan-store).
   */
  mcpIdOverride?: string | null;
  /**
   * When provided, a row click calls this INSTEAD of
   * `useFocusedRequirementStore().setFocused(...)` — a second consumer
   * (the room-assignment overlay, assigning a cart item to a
   * requirement) with a different job than the rail's "focus for the
   * add-affordance." The two must not collide on the same global focus
   * store, so when `onPick` is set, `setFocused` is never also called.
   */
  onPick?: (sourceLineSid: string, label: string | null) => void;
}

export function ObeyaCostPlanRequirementsCard({
  mcpIdOverride, onPick,
}: ObeyaCostPlanRequirementsCardProps = {}) {
  const { mcpId: pageMcpId } = useViewportSelection();
  const pinnedMcpId = usePinnedCostPlanStore((s) => s.pinnedMcpId);
  const mcpId = mcpIdOverride ?? pageMcpId ?? pinnedMcpId;
  const { contentKey } = useCardIdentity();
  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'pivot'));
  const lang = useLanguageStore((s) => s.lang);
  const focusedSourceLineSid = useFocusedRequirementStore((s) => s.focusedSourceLineSid);
  const setFocusedRequirement = useFocusedRequirementStore((s) => s.setFocused);

  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(queryInput), 300);
    return () => clearTimeout(t);
  }, [queryInput]);

  const params = {
    semantic_id: mcpId ?? '',
    group_by: 'line' as const,
    search: query || undefined,
    ...(lang ? { lang } : {}),
  };
  const { data: raw, isLoading, isFetching } = useStrategyData<CostPlanPivotData>(
    strategy, params, !!mcpId && !!strategy,
  );
  const d = raw?.data;
  const L = d?.labels ?? {};
  const groups = d?.groups ?? [];

  const queryKey = ['strategy-data', strategy, params] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_requirements',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: mcpId && strategy ? queryKey : null,
    text: `Cost plan requirements: ${groups.length}`,
    state: { query },
    json: null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy, query, groups.length]));

  if (!mcpId) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        Select a cost plan
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border shrink-0 p-2">
        <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-md px-2.5 py-1.5 flex-1">
          <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder={L.search_table_placeholder ?? ''}
            className="bg-transparent text-xs outline-none w-full"
          />
        </div>
        <span className={cn(text.muted, 'shrink-0')}>{isFetching ? '…' : groups.length}</span>
      </div>
      <div className="flex-1 overflow-auto">
        {isLoading && !d ? (
          <div className={cn('px-3 py-6 text-center', text.muted)}>…</div>
        ) : groups.length === 0 ? (
          <div className={cn('px-3 py-6 text-center', text.muted)}>{L.no_table_match ?? ''}</div>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {groups.map((g) => (
                <RequirementRow
                  key={g.key}
                  g={g}
                  L={L}
                  isFocused={g.key === focusedSourceLineSid}
                  onFocus={() => {
                    if (onPick) {
                      onPick(g.key, g.label || null);
                    } else {
                      setFocusedRequirement(g.key, g.label || null);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// One requirement's glance row — label and qty only, click-to-focus.
// No candidate count, no value/price, no qty input, no expand chevron,
// no room picker: this card never renders a leaf row at all.
function RequirementRow({
  g, L, isFocused, onFocus,
}: {
  g: PivotGroup; L: Record<string, string>; isFocused: boolean; onFocus: () => void;
}) {
  const unassigned = g.key === UNASSIGNED_KEY || g.key.startsWith(`${UNASSIGNED_KEY}:`);
  const label = unassigned ? (L.unassigned ?? '') : (g.label || g.key);
  const canFocus = !unassigned && !g.key.startsWith(NO_SOURCE_LINE_PREFIX);

  const qtyDisplay = g.qty_mismatch
    ? '⚠'
    : (g.total_qty?.toLocaleString() ?? '—');

  return (
    <tr
      className={cn(
        'cursor-pointer border-b border-border/60',
        isFocused ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-muted/40',
      )}
      onClick={() => canFocus && onFocus()}
    >
      <td className={ROW}>
        <div className="flex items-center gap-1.5">
          {canFocus && (
            // Persistent (not hover-only) target affordance, same
            // treatment as the full editor's click-to-focus used to
            // apply conditionally — native here since glance-and-focus
            // is this card's only job.
            <Crosshair
              className={cn('w-3 h-3 shrink-0', isFocused ? 'text-primary' : 'text-muted-foreground/40')}
              aria-hidden
            />
          )}
          <span className={text.title}>{label}</span>
        </div>
      </td>
      <td className={cn(ROW, 'text-right tabular-nums')}>{qtyDisplay}</td>
    </tr>
  );
}
